import { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  HStack,
  Input,
  Select,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

export const CHILD_TICKET_STATUS_COLORS = [
  'gray',
  'red',
  'orange',
  'yellow',
  'green',
  'teal',
  'blue',
  'cyan',
  'purple',
  'pink',
] as const

export type ChildTicketStatusColor = (typeof CHILD_TICKET_STATUS_COLORS)[number]

export type ChildTicketStatusFormProps = {
  statusId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

const initialValues = {
  code: '',
  label: '',
  default_sla_minutes: '' as string | number,
  auto_pause: false,
  triggers_parent_status: '',
  scope: 'generic',
  is_terminal: false,
  color: '' as string,
  sort_order: 0 as string | number,
}

export function ChildTicketStatusForm({
  statusId,
  onSuccess,
  onCancel,
}: ChildTicketStatusFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(!!statusId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)
  const [parentStatusOptions, setParentStatusOptions] = useState<Array<{ code: string; label: string }>>([])
  const [usage, setUsage] = useState<{ tasks: number; template_steps: number } | null>(null)

  const loadOne = useCallback(async () => {
    if (!statusId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`/service-catalog/child-ticket-statuses/${statusId}`)
      const r = data?.data ?? data
      setUsage({
        tasks: Number(r?.usage?.tasks ?? 0),
        template_steps: Number(r?.usage?.template_steps ?? 0),
      })
      setForm({
        code: r.code ?? '',
        label: r.label ?? '',
        default_sla_minutes: r.default_sla_minutes ?? '',
        auto_pause: r.auto_pause === true,
        triggers_parent_status: r.triggers_parent_status ?? '',
        scope: r.scope ?? 'generic',
        is_terminal: r.is_terminal === true,
        color: r.color ?? '',
        sort_order: r.sort_order ?? 0,
      })
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [statusId, showError])

  useEffect(() => {
    if (statusId) {
      loadOne()
    } else {
      setForm(initialValues)
    }
  }, [statusId, loadOne])

  useEffect(() => {
    apiClient
      .get('/service-catalog/parent-ticket-statuses', { params: { per_page: 100 } })
      .then(({ data }) => {
        const rows = Array.isArray(data?.data) ? data.data : []
        setParentStatusOptions(rows.map((row: { code?: string; label?: string }) => ({
          code: row.code ?? '',
          label: row.label ?? row.code ?? '',
        })).filter((row: { code: string }) => row.code !== ''))
      })
      .catch(() => {})
  }, [])

  const updateField = (field: keyof typeof form, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }
  const hasUsage = (usage?.tasks ?? 0) > 0 || (usage?.template_steps ?? 0) > 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const payload = {
        code: form.code.trim(),
        label: form.label.trim() || null,
        default_sla_minutes: form.default_sla_minutes === '' ? null : Number(form.default_sla_minutes),
        auto_pause: form.auto_pause,
        triggers_parent_status: form.triggers_parent_status || null,
        scope: form.scope,
        is_terminal: form.is_terminal,
        color: form.color || null,
        sort_order: form.sort_order === '' ? 0 : Number(form.sort_order),
      }
      if (statusId) {
        await apiClient.put(`/service-catalog/child-ticket-statuses/${statusId}`, payload)
        showSuccess('Success', 'Child ticket status updated successfully')
      } else {
        await apiClient.post('/service-catalog/child-ticket-statuses', payload)
        showSuccess('Success', 'Child ticket status created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') {
        setErrors(res.errors)
      } else {
        showError(err as Error)
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box py={8}>
        <Box as="span">Loading...</Box>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Code <Box as="span" color="red.500">*</Box>
          </FormLabel>
          <FormControl isInvalid={!!errors.code?.length} isRequired>
            <Input
              value={form.code}
              onChange={(e) => updateField('code', e.target.value)}
              placeholder="e.g. IN_PROGRESS"
              isDisabled={!!statusId}
            />
            {!!statusId && (
              <Box as="span" fontSize="sm" color="gray.500">Code cannot be changed after creation.</Box>
            )}
            <FormErrorMessage>{errors.code?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>
        {!!statusId && usage && (
          <Text fontSize="sm" color="gray.600">
            Used by {usage.tasks} tasks, {usage.template_steps} template steps.
          </Text>
        )}

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Label</FormLabel>
          <FormControl isInvalid={!!errors.label?.length}>
            <Input
              value={form.label}
              onChange={(e) => updateField('label', e.target.value)}
              placeholder="e.g. In Progress"
            />
            <FormErrorMessage>{errors.label?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Default SLA (minutes)</FormLabel>
          <FormControl isInvalid={!!errors.default_sla_minutes?.length}>
            <Input
              type="number"
              min={0}
              value={form.default_sla_minutes === '' ? '' : form.default_sla_minutes}
              onChange={(e) => updateField('default_sla_minutes', e.target.value === '' ? '' : e.target.value)}
              placeholder="Optional"
            />
            <FormErrorMessage>{errors.default_sla_minutes?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Triggers parent status</FormLabel>
          <FormControl isInvalid={!!errors.triggers_parent_status?.length}>
            <Select
              value={form.triggers_parent_status}
              onChange={(e) => updateField('triggers_parent_status', e.target.value)}
              placeholder="Select parent status"
            >
              {parentStatusOptions.map((status) => (
                <option key={status.code} value={status.code}>
                  {status.label}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.triggers_parent_status?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Scope</FormLabel>
          <FormControl isInvalid={!!errors.scope?.length}>
            <Select value={form.scope} onChange={(e) => updateField('scope', e.target.value)}>
              <option value="generic">Generic</option>
              <option value="fe_visit">FE Visit</option>
              <option value="parts">Parts</option>
              <option value="permit">Permit</option>
              <option value="custom">Custom</option>
            </Select>
            <FormErrorMessage>{errors.scope?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Color</FormLabel>
          <FormControl isInvalid={!!errors.color?.length}>
            <Wrap spacing={2}>
              <WrapItem>
                <Button
                  size="sm"
                  variant={form.color === '' ? 'solid' : 'outline'}
                  onClick={() => updateField('color', '')}
                >
                  Default
                </Button>
              </WrapItem>
              {CHILD_TICKET_STATUS_COLORS.map((scheme) => (
                <WrapItem key={scheme}>
                  <Button
                    size="sm"
                    colorScheme={scheme}
                    variant={form.color === scheme ? 'solid' : 'outline'}
                    onClick={() => updateField('color', scheme)}
                    aria-label={`Color ${scheme}`}
                  >
                    {scheme}
                  </Button>
                </WrapItem>
              ))}
            </Wrap>
            <FormErrorMessage>{errors.color?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Sort order</FormLabel>
          <FormControl isInvalid={!!errors.sort_order?.length}>
            <Input
              type="number"
              min={0}
              value={form.sort_order === '' ? '' : form.sort_order}
              onChange={(e) => updateField('sort_order', e.target.value === '' ? '' : Number(e.target.value))}
              maxW="160px"
            />
            <FormErrorMessage>{errors.sort_order?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>Auto pause</FormLabel>
          <FormControl>
            <Checkbox
              isChecked={form.auto_pause}
              onChange={(e) => updateField('auto_pause', e.target.checked)}
              colorScheme="blue"
            >
              <Box as="span">Automatically pause SLA when this status is set.</Box>
            </Checkbox>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>Is terminal</FormLabel>
          <FormControl isInvalid={!!errors.is_terminal?.length}>
            <Checkbox
              isChecked={form.is_terminal}
              onChange={(e) => updateField('is_terminal', e.target.checked)}
              colorScheme="blue"
              isDisabled={hasUsage}
            >
              <Box as="span">Marking this terminal affects task completion and reporting logic across the system.</Box>
            </Checkbox>
            {hasUsage && (
              <Text fontSize="xs" color="orange.600" mt={1}>
                Terminal flag cannot be changed while this status is in active use.
              </Text>
            )}
            <FormErrorMessage>{errors.is_terminal?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={saving}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={saving}
            loadingText={statusId ? 'Saving...' : 'Creating...'}
          >
            {statusId ? 'Save' : 'Create Task Status'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
